import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { AttentionInboxItemDto } from '../../api/analytics';
import type { DrillView } from './drillView';
import InboxItemCard from './InboxItemCard';
import ViewRenderer from './ViewRenderer';
import { CATEGORY_BADGE, SEVERITY_DOT } from './inboxShared';

/**
 * THE CASE — the opened work item as a unit of attention (the user's rule:
 * "it can't close when navigating back; it should be sticky and not let you get
 * away until you select one or another status").
 *
 * - Opening a work item starts a CASE: an INLINE panel inside the Darbai tab
 *   (owner rule 2026-09-10: the case locks ITS TAB, never the whole app —
 *   navigation, other tabs and the assistant chat stay usable around it).
 * - Information gathering happens INSIDE: linked items (KUR chips, clickable
 *   document numbers) drill into an internal stack with „← Atgal" — back and
 *   forth freely, the case never leaves.
 * - The case CANNOT be dismissed: no outside-click close, no Escape, no ✗.
 *   The only exit for an actionable item is a STATUS (Atlikta / Atidėti iki… /
 *   Ne problema). Non-actionable items (nothing to decide) get „Uždaryti".
 * - STICKINESS is the parent's job (InboxView): while unresolved, the case is
 *   remembered in sessionStorage and REOPENS whenever Darbai mounts — so even
 *   navigating away doesn't escape it; it's waiting when you return.
 */
export const STICKY_CASE_KEY = 'a5_sticky_case';

interface Props {
  item: AttentionInboxItemDto;
  aiEnabled?: boolean;
  /** A verdict was applied (or a non-actionable close) — clear the case + sticky flag. */
  onResolved: () => void;
}

export default function CaseModal({ item, aiEnabled, onResolved }: Props) {
  const { t } = useTranslation();
  const [drillStack, setDrillStack] = useState<DrillView[]>([]);
  const currentDrill = drillStack[drillStack.length - 1];
  const actionable = !!item.stableKey && !item.automated;
  const overdue = item.daysOverdue !== null && item.daysOverdue > 0;

  // Finding 35: the browser Back button drives the case's internal
  // „← Atgal prie darbo". Every drill INTO a view pushes a same-URL history
  // entry; a popstate pops the visual stack. Without this, browser Back
  // skipped the drill stack entirely and could leave the app (killing the
  // session) while the case was open.
  const selfPopRef = useRef(0);

  useEffect(() => {
    const onPopState = () => {
      if (selfPopRef.current > 0) {
        // Our own history.back() from the internal button — already popped.
        selfPopRef.current -= 1;
        return;
      }
      setDrillStack(stack => (stack.length > 0 ? stack.slice(0, -1) : stack));
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // Finding 35: an actionable case is unfinished work — arm the browser's
  // leave confirmation for close/reload. In-app navigation never loses the
  // case (the sticky flag reopens it), so only real exits warn.
  useEffect(() => {
    if (!actionable) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [actionable]);

  const pushDrillEntry = () => {
    window.history.pushState({ a5drill: true }, '');
  };

  const backToCase = () => {
    setDrillStack(stack => (stack.length > 0 ? stack.slice(0, -1) : stack));
    // Consume this drill's history entry so the browser Back count stays in
    // sync (guarded by selfPopRef — the resulting popstate must not re-pop).
    if ((window.history.state as { a5drill?: boolean } | null)?.a5drill) {
      selfPopRef.current += 1;
      window.history.back();
    }
  };

  return (
    // Inline in the Darbai tab content — no overlay, no backdrop, nothing
    // locked around it (nav, tabs and the docked chat all stay clickable).
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden flex flex-col">
      {/* Case header — who/what, always visible while drilled deep */}
      <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100 bg-white">
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${SEVERITY_DOT[item.severity ?? ''] ?? 'bg-gray-300'}`} />
        <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full flex-shrink-0 ${CATEGORY_BADGE[item.category] ?? 'bg-gray-100 text-gray-600'}`}>
          {t(`analytics.inbox.cat.${item.category}`)}
        </span>
        <span className="flex-1 text-sm font-medium text-gray-900 truncate">{item.title}</span>
        {item.amount != null && (
          <span className={`text-sm font-semibold tabular-nums flex-shrink-0 ${overdue ? 'text-red-600' : 'text-gray-700'}`}>
            {new Intl.NumberFormat('lt-LT', { style: 'currency', currency: 'EUR' }).format(item.amount)}
          </span>
        )}
        {actionable ? (
          <span className="px-2.5 py-1 text-[11px] font-semibold rounded-full bg-amber-100 text-amber-800 border border-amber-200 flex-shrink-0">
            ⏳ {t('analytics.inbox.case.stickyBadge')}
          </span>
        ) : (
          <button
            onClick={onResolved}
            className="px-3 py-1 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:border-gray-400 flex-shrink-0"
          >
            {t('analytics.inbox.case.close')}
          </button>
        )}
      </div>

      {/* Body: the case itself, or the drilled item (info gathering) inside it */}
      <div>
        {currentDrill ? (
          <div className="p-4">
            <button
              onClick={backToCase}
              className="mb-3 inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-lg hover:border-gray-500"
            >
              ← {t('analytics.inbox.case.backToCase')}
            </button>
            <ViewRenderer
              view={currentDrill}
              onDrill={view => { setDrillStack(stack => [...stack, view]); pushDrillEntry(); }}
              aiEnabled={aiEnabled}
            />
          </div>
        ) : (
          <div className="p-5">
            <InboxItemCard
              item={item}
              onDrill={view => { setDrillStack([view]); pushDrillEntry(); }}
              lockOpen
              aiEnabled={aiEnabled}
              onResolved={onResolved}
            />
          </div>
        )}
      </div>
    </div>
  );
}
